import React, { useState, useEffect, useCallback } from "react";
import { Send, Loader2, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const SAMPLE_MESSAGE = `🙏 *Bom dia!*

Hoje a Palavra nos lembra:
_"O Senhor é o meu pastor; nada me faltará."_, Salmos 23:1

Que sua jornada seja guiada por Ele.

, C E Medeiros`;

const PHONE_MASK = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 13);
  if (d.length <= 2) return d ? `+${d}` : "";
  if (d.length <= 4) return `+${d.slice(0, 2)} ${d.slice(2)}`;
  if (d.length <= 9) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4)}`;
  return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
};

/**
 * Formata texto WhatsApp em HTML básico:
 *   *bold* → <strong>
 *   _italic_ → <em>
 *   ~strike~ → <s>
 *   ``` mono ``` → <code>
 * Quebra de linha preservada via white-space:pre-line.
 */
function formatWhatsApp(raw) {
  if (!raw) return "";
  // Escape HTML first
  let s = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  s = s.replace(/```([\s\S]+?)```/g, '<code>$1</code>');
  s = s.replace(/(^|[\s(])\*([^\s*][^*]*?[^\s*]|\S)\*(?=[\s.,;:!?)]|$)/g, '$1<strong>$2</strong>');
  s = s.replace(/(^|[\s(])_([^\s_][^_]*?[^\s_]|\S)_(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');
  s = s.replace(/(^|[\s(])~([^\s~][^~]*?[^\s~]|\S)~(?=[\s.,;:!?)]|$)/g, '$1<s>$2</s>');
  return s;
}

export default function WhatsAppPreview() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState(SAMPLE_MESSAGE);
  const [sending, setSending] = useState(false);
  const [lastSend, setLastSend] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const r = await api.get("/whatsapp/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail || "Falha ao consultar status." });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSend = async () => {
    if (sending) return;
    if (!to.trim()) {
      toast.error("Informe o número de destino.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/whatsapp/test", { to: to.trim(), message });
      setLastSend(r.data);
      if (r.data.success) {
        toast.success(`Enviado! SID: ${r.data.message_sid?.slice(0, 8)}…`);
      } else {
        toast.error(r.data.error || "Falha no envio.");
      }
    } catch (e) {
      const errMsg = e?.response?.data?.detail || "Falha de rede.";
      setLastSend({ success: false, error: errMsg });
      toast.error(errMsg);
    } finally {
      setSending(false);
    }
  };

  const time = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  return (
    <section data-testid="whatsapp-preview" className="bg-white border border-border rounded-2xl p-5 sm:p-6 mb-6">
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold flex items-center gap-1.5">
            WhatsApp · Twilio
          </p>
          <h3 className="font-display text-xl text-navy mt-1">Pré-visualização e envio de teste</h3>
          <p className="text-[12px] text-navy/55 mt-1.5">
            Compõe uma mensagem com formatação WhatsApp (<code>*negrito*</code>, <code>_itálico_</code>, emojis) e envia via Twilio.
          </p>
        </div>
        <button
          type="button"
          onClick={loadStatus}
          disabled={loadingStatus}
          data-testid="whatsapp-status-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-[11px] uppercase tracking-widest text-navy/75 hover:border-gold/60 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${loadingStatus ? "animate-spin" : ""}`} />
          Atualizar status
        </button>
      </header>

      <StatusBadge status={status} loading={loadingStatus} />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-5">
        {/* Composer */}
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-navy/60">Número de destino (E.164)</label>
            <Input
              value={to}
              onChange={(e) => setTo(PHONE_MASK(e.target.value))}
              data-testid="whatsapp-to"
              placeholder="+55 11 98888-7777"
              className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            />
            <p className="text-[10px] text-navy/45 mt-1.5">
              Sandbox: o destinatário precisa enviar <code>join &lt;código&gt;</code> ao número Sandbox antes do 1º envio.
            </p>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-navy/60">Mensagem ({message.length}/1500)</label>
            <Textarea
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 1500))}
              data-testid="whatsapp-message"
              className="mt-2 bg-[hsl(var(--parchment))] border-transparent focus:bg-white font-mono text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !to.trim() || !message.trim() || !status?.connected}
            data-testid="whatsapp-send-test"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 px-5 py-3 rounded-full text-[11px] uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar teste agora
          </button>
          {lastSend && (
            <div
              data-testid="whatsapp-last-result"
              className={`rounded-xl p-3 text-[12px] border ${lastSend.success ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
            >
              {lastSend.success ? (
                <>Enviado para <code>{lastSend.to}</code> · SID: <code>{lastSend.message_sid}</code></>
              ) : (
                <>Falha: {lastSend.error}</>
              )}
            </div>
          )}
        </div>

        {/* Preview bubble */}
        <div className="lg:sticky lg:top-4 self-start">
          <p className="text-[10px] uppercase tracking-widest text-navy/55 mb-2">Pré-visualização</p>
          <div className="bg-[#e5ddd5] rounded-2xl p-4 min-h-[200px] flex flex-col justify-end">
            <div
              data-testid="whatsapp-bubble"
              className="bg-[#dcf8c6] rounded-2xl rounded-br-md px-3.5 py-2.5 max-w-full ml-auto shadow-sm"
              style={{ wordBreak: "break-word" }}
            >
              <p
                className="text-[14px] text-[#111] leading-relaxed"
                style={{ whiteSpace: "pre-line" }}
                dangerouslySetInnerHTML={{ __html: formatWhatsApp(message) || '<span class="text-black/40">(sua mensagem aparece aqui)</span>' }}
              />
              <p className="text-[10px] text-black/45 text-right mt-1">{time} ✓</p>
            </div>
          </div>
          <p className="text-[10px] text-navy/40 mt-2 text-right">via Twilio Sandbox</p>
        </div>
      </div>
    </section>
  );
}

function StatusBadge({ status, loading }) {
  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-[11px]">
        <Loader2 className="w-3 h-3 animate-spin" /> Verificando conexão…
      </div>
    );
  }
  if (!status) return null;
  const ok = status.connected;
  return (
    <div className="space-y-2" data-testid="whatsapp-status-badge" data-state={ok ? "connected" : "error"}>
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] ${
        ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"
      }`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        {ok
          ? <>Conectado · {status.account || "Twilio"} · {status.from_number}</>
          : <>Desconectado{status.error ? `, ${status.error}` : ""}</>}
      </div>
      {!ok && (
        <details className="text-[11px] text-navy/55">
          <summary className="cursor-pointer hover:text-navy">Detalhes da configuração</summary>
          <ul className="mt-2 space-y-0.5 pl-4">
            <li>SID: <code>{status.sid_masked || ", "}</code> · {status.sid_configured ? "configurado" : "ausente"}</li>
            <li>Token: <code>{status.token_masked || ", "}</code> · {status.token_configured ? "configurado" : "ausente"}</li>
            <li>From: <code>{status.from_number || ", "}</code> · {status.from_number_valid ? "formato OK" : "formato inválido"}</li>
          </ul>
        </details>
      )}
    </div>
  );
}
